import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTranslation } from 'react-i18next';
import { fetchMyDoctors, fetchPatientAppointments, fetchPatientProfile, updatePatientProfile, cancelAppointment, rescheduleAppointment, consultations } from "../../services/api";
import "./patientDashboard.css";
import Messages from "../Messages/Messages";

export default function PatientDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("appointments");
  const [assignedDoctor, setAssignedDoctor] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [profile, setProfile] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [consultationStatus, setConsultationStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      fetchMyDoctors(),
      fetchPatientAppointments(),
      fetchPatientProfile(),
      consultations.getMyLatest()
    ]).then(([doctors, appts, prof, consult]) => {
      if (doctors && doctors.length > 0) setAssignedDoctor(doctors[0]);
      setAppointments(appts);
      if (consult) setConsultationStatus(consult);
      setProfile({
        first_name: prof.first_name || "",
        last_name: prof.last_name || "",
        phone: prof.phone || "",
        email: user?.email || ""
      });
    }).catch(err => console.error("Error fetching dashboard data:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await updatePatientProfile(profile);
      alert(t('patient.settings.save_success', 'Profile updated successfully!'));
    } catch (err) {
      console.error("Update failed:", err);
      alert(t('patient.settings.save_error', 'Failed to update profile.'));
    }
  };

  // helpers for date display
  const formatDate = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleDateString();
  };
  const formatTime = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const isPast = (isoStr) => new Date(isoStr).getTime() < Date.now();

  // ---- Reschedule modal ---- (Keep logic but update for API)
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  const openReschedule = (id) => {
    const appt = appointments.find(a => a.id === id);
    if (!appt) return;
    setEditId(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
  };

  const saveReschedule = async () => {
    if (!newDate || !newTime) return alert("Please select date and time");
    try {
      await rescheduleAppointment(editId, newDate, newTime);
      alert(t('patient.appointments.reschedule_success', 'Appointment rescheduled successfully.'));
      fetchData();
      closeModal();
    } catch (err) {
      console.error("Reschedule failed:", err);
      alert(t('patient.appointments.reschedule_error', 'Failed to reschedule appointment.'));
    }
  };

  // ---- Cancel modal ----
  const [showCancel, setShowCancel] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const openCancel = (id) => {
    setCancelId(id);
    setShowCancel(true);
  };

  const closeCancel = () => {
    setShowCancel(false);
    setCancelId(null);
  };

  const confirmCancel = async () => {
    try {
      await cancelAppointment(cancelId);
      alert(t('patient.appointments.cancel_success', 'Appointment cancelled successfully.'));
      fetchData();
      setShowCancel(false);
    } catch (err) {
      console.error("Cancel failed:", err);
      alert(t('patient.appointments.cancel_error', 'Failed to cancel appointment.'));
    }
  };

  const handleRequestConsultation = async () => {
    if (!window.confirm("Demander une consultation ?")) return;
    try {
      await consultations.request();
      alert("Demande envoyée avec succès !");
      fetchData();
    } catch (err) {
      console.error("Request failed:", err);
      alert("Erreur lors de la demande: " + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <div className="dashboard-loading">Loading...</div>;

  return (
    <section className="dashboard">
      <header className="dashboard-header">
        <h1>{t('patient.dashboard_title', 'Patient Dashboard')}</h1>
        <p>{t('patient.dashboard_subtitle', 'Welcome back, manage your appointments, messages, and settings.')}</p>

        <div className="assigned-doctor-banner">
          <h3>My Care Team</h3>
          <p><strong>Dr. {assignedDoctor.first_name} {assignedDoctor.last_name}</strong> — {assignedDoctor.specialty || 'Specialist'}</p>

          {!consultationStatus && appointments.length === 0 && (
            <div className="alert-box">
              <p>Bienvenue ! Votre première étape est de demander une consultation initiale.</p>
              <button className="btn primary" onClick={handleRequestConsultation}>Demander une consultation</button>
            </div>
          )}

          {consultationStatus && consultationStatus.status === 'PENDING' && (
            <div className="alert-box warning">
              <p>⏳ Votre demande de consultation est en attente de planification par l'administrateur.</p>
            </div>
          )}

          {consultationStatus && consultationStatus.status === 'SCHEDULED' && (
            <div className="alert-box success">
              <p>📅 Consultation planifiée le {formatDate(consultationStatus.scheduled_date)} à {formatTime(consultationStatus.scheduled_date)}.</p>
              <button className="btn primary" onClick={() => navigate('/consultations')}>Voir détails</button>
            </div>
          )}
        </div>
      </header>

      <nav className="dashboard-tabs">
        <button className={activeTab === "appointments" ? "active" : ""} onClick={() => setActiveTab("appointments")}>{t('patient.tabs.appointments', 'Appointments')}</button>
        <button className={activeTab === "messages" ? "active" : ""} onClick={() => setActiveTab("messages")}>{t('patient.tabs.messages', 'Messages')}</button>
        <button className={activeTab === "settings" ? "active" : ""} onClick={() => setActiveTab("settings")}>{t('patient.tabs.settings', 'Settings')}</button>
      </nav>

      <div className="dashboard-content">
        {activeTab === "appointments" && (
          <div className="appointments">
            <h2>{t('patient.appointments.upcoming', 'Upcoming Appointments')}</h2>
            {appointments.length === 0 ? (
              <p className="muted">No appointments found.</p>
            ) : (
              appointments.map((a) => {
                const effectiveStatus = a.status === "cancelled" ? "Cancelled" : isPast(a.start_time) ? "Passed" : a.status;
                return (
                  <div key={a.id} className="appointment-card">
                    <div>
                      <strong>Dr. {a.doctor_first_name} {a.doctor_last_name}</strong>
                      <p>{formatDate(a.start_time)} — {formatTime(a.start_time)}</p>
                    </div>

                    <span className={`status ${effectiveStatus.toLowerCase()}`}>{t(`patient.status.${effectiveStatus.toLowerCase()}`, effectiveStatus)}</span>

                    <div className="actions">
                      <button className="btn ghost" onClick={() => openReschedule(a.id)}>{t('patient.action.reschedule', 'Reschedule')}</button>
                      <button className="btn danger" onClick={() => openCancel(a.id)}>{t('patient.action.cancel', 'Cancel')}</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === "messages" && (
          <Messages />
        )}

        {activeTab === "settings" && (
          <div className="settings">
            <h2>{t('patient.settings.title', 'Profile Settings')}</h2>
            <form className="settings-form" onSubmit={handleProfileUpdate}>
              <label>
                {t('patient.form.first_name', 'First Name')}
                <input
                  type="text"
                  value={profile.first_name}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder={t('patient.form.name_placeholder', 'Your first name')}
                />
              </label>
              <label>
                {t('patient.form.last_name', 'Last Name')}
                <input
                  type="text"
                  value={profile.last_name}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder={t('patient.form.name_placeholder', 'Your last name')}
                />
              </label>
              <label>
                {t('patient.form.email', 'Email')}
                <input type="email" value={profile.email} disabled />
              </label>
              <label>
                {t('patient.form.phone', 'Phone')}
                <input
                  type="text"
                  value={profile.phone}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })}
                  placeholder={t('patient.form.phone_placeholder', '+216 99 123 456')}
                />
              </label>
              <button type="submit" className="btn primary">{t('patient.form.save', 'Save Changes')}</button>
            </form>
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {showModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Reschedule appointment</h3>
            <div className="modal-grid">
              <label>
                New date
                <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
              </label>
              <label>
                New time
                <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn ghost" onClick={saveReschedule}>Save</button>
              <button className="btn danger" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancel && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Cancel appointment</h3>
            <p className="muted">Optionally add a reason. You can’t undo this.</p>
            <div className="modal-grid single">
              <label>
                Reason (optional)
                <textarea
                  rows="4"
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="e.g. Feeling better, scheduling conflict…"
                />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn danger" onClick={confirmCancel}>Yes, cancel</button>
              <button className="btn ghost" onClick={closeCancel}>Keep appointment</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
